import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
	createUser,
	deleteUser,
	generateUserPassword,
	getStudentProfile,
	listUsers,
	updateUser,
} from "../api/users";
import { capitalizeFirstLetter, capitalizeWords } from "../../utils/letters";
import { listCompanies, resetCompanyAdminPassword } from "../api/companies";
import { formatDate, formatDateTime } from "../../utils/date";
import { translateStatusLabel } from "../../utils/statusLabels";
import {
	INVALID_RUT_MESSAGE,
	RUT_DIGITS_HINT,
	cleanRut,
	commitRutField,
	formatRut,
	sanitizeRutDigitsOnly,
	toApiRut,
	validateRut,
} from "../../utils/rutUtils";
import Modal from "../components/Modal";
import ConfirmModal from "../components/ConfirmModal";
import StatusBadge from "../components/StatusBadge";

const emptyForm = {
	username: "",
	first_name: "",
	last_name: "",
	email: "",
	rut: "",
	role: "",
	is_active: true,
	company: "",
};

const ROLE_LABEL = {
	admin: "Admin",
	teacher: "Docente",
	student: "Estudiante",
	company_admin: "Organización",
};

const REQUIRED_RUT_MESSAGE = "El RUT es obligatorio.";
const USERS_ROWS_PER_PAGE_DEFAULT = 10;
const USERS_ALLOWED_ROWS = [10, 25, 50, 100];

const USER_SORT_OPTIONS = [
	{ value: "rut", label: "RUT" },
	{ value: "full_name", label: "Nombre" },
	{ value: "role", label: "Rol" },
	{ value: "organization", label: "Organización" },
	{ value: "is_active", label: "Activo" },
];

const USER_ALLOWED_SORTS = new Set(
	USER_SORT_OPTIONS.map((option) => option.value),
);
const USER_ALLOWED_DIRECTIONS = new Set(["asc", "desc"]);
const USER_ALLOWED_TABS = new Set([
	"admin",
	"company_admin",
	"teacher",
	"student",
	"",
	"all",
]);

function parsePositiveInt(rawValue, fallbackValue) {
	const parsed = Number(rawValue);
	if (!Number.isInteger(parsed) || parsed < 1) return fallbackValue;
	return parsed;
}

function parseRows(rawValue) {
	const parsed = Number(rawValue);
	if (!USERS_ALLOWED_ROWS.includes(parsed)) return USERS_ROWS_PER_PAGE_DEFAULT;
	return parsed;
}

function parseSort(rawValue) {
	if (!USER_ALLOWED_SORTS.has(rawValue)) return "full_name";
	return rawValue;
}

function parseDirection(rawValue) {
	if (!USER_ALLOWED_DIRECTIONS.has(rawValue)) return "asc";
	return rawValue;
}

function parseShowInactive(rawValue) {
	return rawValue === "1";
}

function parseTab(rawValue) {
	if (!USER_ALLOWED_TABS.has(rawValue)) return "admin";
	return rawValue;
}

const ROLE_DETAIL_LABEL = {
	admin: "administrador",
	company_admin: "representante",
	teacher: "docente",
	student: "estudiante",
};

function hasRequiredValue(value) {
	return String(value ?? "").trim() !== "";
}

function validateLettersAndSpaces(value) {
	if (!value || !String(value).trim()) return true;
	return /^[a-zA-ZáéíóúÁÉÍÓÚñÑüÜ\s]+$/.test(String(value).trim());
}

function getFirstApiMessage(value) {
	if (Array.isArray(value)) return value[0] ?? "";
	if (typeof value === "string") return value;
	return "";
}

function normalizeUserRutMessage(message) {
	if (!message) return "";
	if (/^el rut ya est[aá] registrado\.?$/i.test(message)) {
		return "Ya existe usuario con este rut.";
	}
	return message;
}

function getApiFieldErrors(data) {
	const next = {};
	if (!data || typeof data !== "object") return next;

	const rutMsg = normalizeUserRutMessage(getFirstApiMessage(data.rut));
	const emailMsg = getFirstApiMessage(data.email);
	const usernameMsg = getFirstApiMessage(data.username);
	const firstNameMsg = getFirstApiMessage(data.first_name);
	const lastNameMsg = getFirstApiMessage(data.last_name);
	const roleMsg = getFirstApiMessage(data.role);
	const companyMsg = getFirstApiMessage(data.company);

	if (rutMsg) next.rut = rutMsg;
	if (emailMsg) next.email = emailMsg;
	if (usernameMsg) next.username = usernameMsg;
	if (firstNameMsg) next.first_name = firstNameMsg;
	if (lastNameMsg) next.last_name = lastNameMsg;
	if (roleMsg) next.role = roleMsg;
	if (companyMsg) next.company = companyMsg;

	const detailRaw =
		typeof data.detail === "string"
			? data.detail
			: Array.isArray(data.non_field_errors)
				? data.non_field_errors[0]
				: "";
	const detail = normalizeUserRutMessage(detailRaw);

	if (!next.rut && /\brut\b/i.test(detail)) next.rut = detail;
	if (!next.email && /(correo|email)/i.test(detail)) next.email = detail;
	if (!next.username && /(usuario|username)/i.test(detail)) {
		next.username = detail;
	}
	if (!next.role && /\brol\b/i.test(detail)) next.role = detail;
	if (!next.company && /(organizacion|company)/i.test(detail)) {
		next.company = detail;
	}

	return next;
}

function getUserFieldColumnClass(editing) {
	return editing ? "col-md-4" : "col-md-6";
}

function getCompanyId(value) {
	if (typeof value === "object" && value !== null) return value.id;
	const parsed = Number(value);
	return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
}

function getOrganizationDisplayName(user, companies) {
	if (user?.role === "admin" || user?.role === "teacher") {
		return "Somnus";
	}

	if (!user?.company) return "—";

	const companyMatch = companies.find(
		(company) => company.id === user.company,
	);
	return capitalizeFirstLetter(companyMatch?.name ?? `#${user.company}`);
}

function getOrganizationData(user, companies) {
	if (!user) {
		return { name: "-", rut: "-" };
	}

	if (user.role === "admin" || user.role === "teacher") {
		const somnus = companies.find((company) =>
			String(company?.name || "").toLowerCase().includes("somnus"),
		);
		return {
			name: capitalizeFirstLetter(somnus?.name || "Somnus"),
			rut: formatRut(somnus?.rut) || somnus?.rut || "-",
		};
	}

	if (!user.company) {
		return { name: "-", rut: "-" };
	}

	if (typeof user.company === "object") {
		return {
			name: capitalizeFirstLetter(user.company?.name || "-") || "-",
			rut: formatRut(user.company?.rut) || user.company?.rut || "-",
		};
	}

	const companyMatch = companies.find((company) => company.id === user.company);
	return {
		name: capitalizeFirstLetter(companyMatch?.name ?? `#${user.company}`),
		rut: formatRut(companyMatch?.rut) || companyMatch?.rut || "-",
	};
}

function getUserCompany(user, companies) {
	if (!user?.company || typeof user.company === "object") {
		return user?.company || null;
	}
	return companies.find((company) => company.id === user.company) || null;
}

function isUserReactivationBlocked(user, companies) {
	const company = getUserCompany(user, companies);
	return Boolean(company && company.is_active === false);
}

function statusChipClass(value) {
	const normalized = String(value || "").toLowerCase();
	if (["finished", "completed", "active", "open", "passed"].includes(normalized)) {
		return "student-profile-chip student-profile-chip--ok";
	}
	if (["cancelled", "failed", "revoked", "closed"].includes(normalized)) {
		return "student-profile-chip student-profile-chip--danger";
	}
	return "student-profile-chip student-profile-chip--neutral";
}

function normalizeRutLike(value) {
	return (value ?? "")
		.toString()
		.toLowerCase()
		.replaceAll(/[.\-\s]/g, "");
}

function escapeRegExp(value) {
	return value.replaceAll(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`);
}

function highlightSearchMatch(value, query) {
	const text = (value ?? "").toString();
	const needle = query.trim();
	if (!needle) return text;

	const matcher = new RegExp(`(${escapeRegExp(needle)})`, "ig");
	const parts = text.split(matcher);
	let cursor = 0;

	return parts.map((part) => {
		const key = `${cursor}-${part}`;
		cursor += part.length;
		if (part.toLowerCase() === needle.toLowerCase()) {
			return (
				<mark className="search-highlight" key={`hit-${key}`}>
					{part}
				</mark>
			);
		}
		return <span key={`txt-${key}`}>{part}</span>;
	});
}

function highlightRutSearchMatch(value, query) {
	const text = (value ?? "").toString();
	const needle = query.trim();
	if (!needle) return text;

	const normalizedNeedle = normalizeRutLike(needle);
	if (!normalizedNeedle) return highlightSearchMatch(text, needle);

	const normalizedText = [];
	const originalIndexes = [];
	for (let idx = 0; idx < text.length; idx += 1) {
		const char = text[idx];
		if (/[.\-\s]/.test(char)) continue;
		normalizedText.push(char.toLowerCase());
		originalIndexes.push(idx);
	}

	const compact = normalizedText.join("");
	const start = compact.indexOf(normalizedNeedle);
	if (start === -1) return highlightSearchMatch(text, needle);

	const originalStart = originalIndexes[start];
	const originalEnd =
		originalIndexes[start + normalizedNeedle.length - 1] + 1;

	const before = text.slice(0, originalStart);
	const hit = text.slice(originalStart, originalEnd);
	const after = text.slice(originalEnd);

	return (
		<>
			<span>{before}</span>
			<mark className="search-highlight">{hit}</mark>
			<span>{after}</span>
		</>
	);
}

export default function UsersList() {
	const [searchParams, setSearchParams] = useSearchParams();
	const [items, setItems] = useState([]);
	const [search, setSearch] = useState(() => searchParams.get("q") || "");
	const [loading, setLoading] = useState(false);
	const [rowsPerPage, setRowsPerPage] = useState(() =>
		parseRows(searchParams.get("rows")),
	);
	const [currentPage, setCurrentPage] = useState(() =>
		parsePositiveInt(searchParams.get("page"), 1),
	);
	const [sortField, setSortField] = useState(() =>
		parseSort(searchParams.get("sort")),
	);
	const [sortDirection, setSortDirection] = useState(() =>
		parseDirection(searchParams.get("dir")),
	);
	const [alert, setAlert] = useState(null);

	const [modalOpen, setModalOpen] = useState(false);
	const [editing, setEditing] = useState(null);
	const [form, setForm] = useState(emptyForm);
	const [saving, setSaving] = useState(false);
	const [rutError, setRutError] = useState("");
	const [fieldErrors, setFieldErrors] = useState({});
	const [credentialResult, setCredentialResult] = useState(null);
	const [companies, setCompanies] = useState([]);
	const [userToDelete, setUserToDelete] = useState(null);
	const [deleteLoading, setDeleteLoading] = useState(false);
	const [userToActivate, setUserToActivate] = useState(null);
	const [activateLoading, setActivateLoading] = useState(false);
	const [activateError, setActivateError] = useState("");
	const [activeTab, setActiveTab] = useState(() =>
		parseTab(searchParams.get("tab")),
	);
	const [showInactiveUsers, setShowInactiveUsers] = useState(() =>
		parseShowInactive(searchParams.get("inactive")),
	);
	const [detailUser, setDetailUser] = useState(null);
	const [detailLoading, setDetailLoading] = useState(false);
	const [detailError, setDetailError] = useState("");
	const [detailData, setDetailData] = useState(null);
	const [resetPasswordTarget, setResetPasswordTarget] = useState(null);
	const [resetPasswordLoading, setResetPasswordLoading] = useState(false);
	const [resetPasswordResult, setResetPasswordResult] = useState(null);
	const selectableCompanies = useMemo(() => {
		const activeCompanies = companies.filter((company) => company.is_active);
		const currentCompanyId = getCompanyId(editing?.company ?? form.company);
		if (!currentCompanyId) return activeCompanies;
		const currentCompany = companies.find((company) => company.id === currentCompanyId);
		if (!currentCompany || currentCompany.is_active) return activeCompanies;
		return [...activeCompanies, currentCompany];
	}, [companies, editing?.company, form.company]);

	function canResetPassword(user) {
		return user?.role === "teacher" || user?.role === "company_admin";
	}

	function getCompanyIdForReset(user) {
		const rawCompany =
			typeof user?.company === "object" ? user.company?.id : user?.company;
		const parsed = Number(rawCompany ?? form.company ?? 0);
		if (!Number.isInteger(parsed) || parsed < 1) return null;
		return parsed;
	}

	async function refresh() {
		setLoading(true);
		try {
			const data = await listUsers({});
			setItems(Array.isArray(data) ? data : (data.results ?? []));
		} catch {
			setAlert({
				type: "danger",
				msg: "No se pudieron cargar los usuarios.",
			});
		} finally {
			setLoading(false);
		}
	}

	useEffect(() => {
		refresh();
		(async () => {
			try {
				const data = await listCompanies({});
				setCompanies(Array.isArray(data) ? data : (data.results ?? []));
			} catch {
				setCompanies([]);
			}
		})();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const filtered = useMemo(() => {
		const visibleItems = showInactiveUsers
			? items
			: items.filter((u) => u.is_active !== false);
		const q = search.trim().toLowerCase();
		if (!q) return visibleItems;
		const normalizedQuery = normalizeRutLike(q);
		return visibleItems.filter((u) => {
			const formattedRut = formatRut(u.rut) || u.rut || "";
			const fullName =
				`${u.first_name || ""} ${u.last_name || ""}`.trim();
			const organizationName = getOrganizationDisplayName(u, companies);
			const roleLabel = ROLE_LABEL[u.role] || u.role || "";
			return (
				(u.first_name ?? "").toLowerCase().includes(q) ||
				(u.last_name ?? "").toLowerCase().includes(q) ||
				fullName.toLowerCase().includes(q) ||
				(u.rut ?? "").toLowerCase().includes(q) ||
				(u.role ?? "").toLowerCase().includes(q) ||
				roleLabel.toLowerCase().includes(q) ||
				organizationName.toLowerCase().includes(q) ||
				(normalizedQuery.length > 0 &&
					normalizeRutLike(formattedRut).includes(normalizedQuery))
			);
		});
	}, [companies, items, search, showInactiveUsers]);

	const tabFilteredUsers = useMemo(() => {
		const byRole =
			activeTab === "all"
				? filtered
				: filtered.filter((u) => (u.role || "") === activeTab);

		if (activeTab !== "") {
			return byRole;
		}

		// Evita mostrar filas "fantasma" en Sin rol asignado cuando el registro
		// no tiene datos mínimos de identidad.
		return byRole.filter((u) => {
			const fullName = `${u.first_name || ""} ${u.last_name || ""}`.trim();
			return [u.username, u.email, u.rut, fullName].some(hasRequiredValue);
		});
	}, [activeTab, filtered]);

	const sortedUsers = useMemo(() => {
		const data = [...tabFilteredUsers];
		data.sort((left, right) => {
			let leftValue = "";
			let rightValue = "";

			if (sortField === "is_active") {
				leftValue = left.is_active ? 1 : 0;
				rightValue = right.is_active ? 1 : 0;
			} else if (sortField === "full_name") {
				leftValue = `${left.first_name || ""} ${left.last_name || ""}`
					.trim()
					.toLowerCase();
				rightValue =
					`${right.first_name || ""} ${right.last_name || ""}`
						.trim()
						.toLowerCase();
			} else if (sortField === "organization") {
				leftValue = getOrganizationDisplayName(
					left,
					companies,
				).toLowerCase();
				rightValue = getOrganizationDisplayName(
					right,
					companies,
				).toLowerCase();
			} else if (sortField === "rut") {
				leftValue = normalizeRutLike(
					formatRut(left.rut) || left.rut || "",
				);
				rightValue = normalizeRutLike(
					formatRut(right.rut) || right.rut || "",
				);
			} else {
				leftValue = (left[sortField] ?? "").toString().toLowerCase();
				rightValue = (right[sortField] ?? "").toString().toLowerCase();
			}

			if (leftValue !== rightValue) {
				if (typeof leftValue === "number" && typeof rightValue === "number") {
					return sortDirection === "asc"
						? leftValue - rightValue
						: rightValue - leftValue;
				}
				if (sortDirection === "asc") {
					return leftValue > rightValue ? 1 : -1;
				}
				return leftValue < rightValue ? 1 : -1;
			}

			const leftActiveRank = left.is_active === false ? 1 : 0;
			const rightActiveRank = right.is_active === false ? 1 : 0;
			if (leftActiveRank !== rightActiveRank) {
				return leftActiveRank - rightActiveRank;
			}
			return 0;
		});
		return data;
	}, [companies, tabFilteredUsers, sortDirection, sortField]);

	useEffect(() => {
		const nextSearch = searchParams.get("q") || "";
		const nextRows = parseRows(searchParams.get("rows"));
		const nextPage = parsePositiveInt(searchParams.get("page"), 1);
		const nextSort = parseSort(searchParams.get("sort"));
		const nextDirection = parseDirection(searchParams.get("dir"));
		const nextTab = parseTab(searchParams.get("tab"));
		const nextShowInactive = parseShowInactive(searchParams.get("inactive"));

		setSearch((prev) => (prev === nextSearch ? prev : nextSearch));
		setRowsPerPage((prev) => (prev === nextRows ? prev : nextRows));
		setCurrentPage((prev) => (prev === nextPage ? prev : nextPage));
		setSortField((prev) => (prev === nextSort ? prev : nextSort));
		setSortDirection((prev) =>
			prev === nextDirection ? prev : nextDirection,
		);
		setActiveTab((prev) => (prev === nextTab ? prev : nextTab));
		setShowInactiveUsers((prev) =>
			prev === nextShowInactive ? prev : nextShowInactive,
		);
	}, [searchParams]);

	useEffect(() => {
		setCurrentPage(1);
	}, [search, activeTab, rowsPerPage, sortField, sortDirection, showInactiveUsers]);

	const totalRows = sortedUsers.length;
	const totalPages = Math.max(1, Math.ceil(totalRows / rowsPerPage));

	useEffect(() => {
		if (currentPage > totalPages) {
			setCurrentPage(totalPages);
		}
	}, [currentPage, totalPages]);

	useEffect(() => {
		const nextParams = new URLSearchParams(searchParams);

		if (search.trim()) nextParams.set("q", search.trim());
		else nextParams.delete("q");

		if (rowsPerPage !== USERS_ROWS_PER_PAGE_DEFAULT) {
			nextParams.set("rows", String(rowsPerPage));
		} else {
			nextParams.delete("rows");
		}

		if (currentPage !== 1) nextParams.set("page", String(currentPage));
		else nextParams.delete("page");

		if (sortField !== "full_name") nextParams.set("sort", sortField);
		else nextParams.delete("sort");

		if (sortDirection !== "asc") nextParams.set("dir", sortDirection);
		else nextParams.delete("dir");

		if (activeTab !== "admin") nextParams.set("tab", activeTab);
		else nextParams.delete("tab");

		if (showInactiveUsers) nextParams.set("inactive", "1");
		else nextParams.delete("inactive");

		if (nextParams.toString() !== searchParams.toString()) {
			setSearchParams(nextParams, { replace: true });
		}
	}, [
		search,
		rowsPerPage,
		currentPage,
		sortField,
		sortDirection,
		activeTab,
		showInactiveUsers,
		searchParams,
		setSearchParams,
	]);

	function resetTableState() {
		setSearch("");
		setRowsPerPage(USERS_ROWS_PER_PAGE_DEFAULT);
		setCurrentPage(1);
		setSortField("full_name");
		setSortDirection("asc");
		setActiveTab("admin");
		setShowInactiveUsers(false);
		setSearchParams(new URLSearchParams(), { replace: true });
	}

	const startIndex = (currentPage - 1) * rowsPerPage;
	const endIndex = Math.min(startIndex + rowsPerPage, totalRows);
	const usersToShow = sortedUsers.slice(startIndex, startIndex + rowsPerPage);
	function openCreate() {
		setEditing(null);
		setForm(emptyForm);
		setAlert(null);
		setRutError("");
		setFieldErrors({});
		setCredentialResult(null);
		setResetPasswordResult(null);
		setModalOpen(true);
	}

	function openEdit(user) {
		setEditing(user);
		setForm({
			first_name: user.first_name ?? "",
			last_name: user.last_name ?? "",
			email: user.email ?? "",
			rut: sanitizeRutDigitsOnly(cleanRut(user.rut ?? "")),
			role: user.role ?? "",
			is_active: user.is_active ?? true,
			company: user.company ?? "",
		});
		setAlert(null);
		setRutError("");
		setFieldErrors({});
		setCredentialResult(null);
		setResetPasswordResult(null);
		setModalOpen(true);
	}

	function validateForm() {
		const err = {};
		if (!hasRequiredValue(form.rut)) {
			err.rut = REQUIRED_RUT_MESSAGE;
		} else if (form.rut && !validateRut(form.rut).valid) {
			err.rut = INVALID_RUT_MESSAGE;
		}
		if (!hasRequiredValue(form.email))
			err.email = "El correo es obligatorio.";
		if (!editing) {
			if (form.role !== "admin" && form.role !== "teacher") {
				err.role = "Debes elegir Administrador o Docente.";
			}
		} else if (!hasRequiredValue(form.role)) {
			err.role = "Debes elegir un rol.";
		}
		if (!hasRequiredValue(form.first_name))
			err.first_name = "El nombre es obligatorio.";
		if (!(form.rut ?? "").trim()) err.rut = "El RUT es obligatorio.";
		if (!(form.last_name ?? "").trim())
			err.last_name = "Los apellidos son obligatorios.";

		setFieldErrors(err);
		setRutError(err.rut ?? "");
		return Object.keys(err).length === 0;
	}

	function closeModal() {
		if (saving) return;
		setModalOpen(false);
		setFieldErrors({});
		setCredentialResult(null);
		setResetPasswordResult(null);
	}

	function askResetPassword(user) {
		if (!canResetPassword(user)) return;
		setResetPasswordTarget(user);
	}

	async function confirmResetPassword() {
		if (!resetPasswordTarget) return;

		setResetPasswordLoading(true);
		setAlert(null);
		try {
			let response = null;
			if (resetPasswordTarget.role === "company_admin") {
				const companyId = getCompanyIdForReset(resetPasswordTarget);
				if (!companyId) {
					throw new Error("No fue posible identificar la organización del representante.");
				}
				response = await resetCompanyAdminPassword(
					companyId,
					resetPasswordTarget.id,
				);
				setResetPasswordResult({
					username:
						response?.user?.username || resetPasswordTarget.username || "-",
					tempPassword: response?.temp_password || "-",
				});
			} else {
				response = await generateUserPassword(resetPasswordTarget.id);
				setResetPasswordResult({
					username: resetPasswordTarget.username || "-",
					tempPassword: response?.temp_password || "-",
				});
			}

			setAlert({
				type: "success",
				msg: "Contraseña temporal generada correctamente.",
			});
			setResetPasswordTarget(null);
			await refresh();
		} catch (error) {
			setAlert({
				type: "danger",
				msg:
					error?.response?.data?.detail ||
					error?.response?.data?.error ||
					error?.message ||
					"No se pudo reiniciar la contraseña.",
			});
		} finally {
			setResetPasswordLoading(false);
		}
	}

	async function onSubmit(e) {
		e.preventDefault();
		if (!validateForm()) return;

		const normalizedRut = toApiRut(form.rut);
		if (!validateLettersAndSpaces(form.first_name)) {
			setFieldErrors((p) => ({
				...p,
				first_name: "Nombre debe contener solo letras y espacios.",
			}));
			return;
		}
		if (!validateLettersAndSpaces(form.last_name)) {
			setFieldErrors((p) => ({
				...p,
				last_name: "Apellidos debe contener solo letras y espacios.",
			}));
			return;
		}

		setSaving(true);
		setAlert(null);
		try {
			const companyPayload =
				form.role === "admin" || form.role === "teacher"
					? null
					: form.company || null;
			const payload = {
				...form,
				rut: normalizedRut,
				company: companyPayload,
			};

			if (editing) {
				await updateUser(editing.id, payload);
				setAlert({
					type: "success",
					msg: "Usuario actualizado correctamente.",
				});
				setModalOpen(false);
			} else {
				const createdUser = await createUser(payload);
				setCredentialResult({
					username: createdUser?.username || "-",
					tempPassword:
						createdUser?.temp_password ??
						createdUser?.generated_password ??
						"-",
				});
			}
			setRutError("");
			setFieldErrors({});
			await refresh();
		} catch (e) {
			const apiData = e.response?.data;
			const apiFieldErrors = getApiFieldErrors(apiData);
			setFieldErrors((prev) => ({ ...prev, ...apiFieldErrors }));
			if (apiFieldErrors.rut) {
				setRutError(apiFieldErrors.rut);
			}

			const fallbackMsg =
				typeof apiData?.detail === "string"
					? apiData.detail
					: "No se pudo guardar. Revisa RUT, correo y los campos obligatorios.";

			if (Object.keys(apiFieldErrors).length === 0) {
				setAlert({
					type: "danger",
					msg: fallbackMsg,
				});
			} else {
				setAlert(null);
			}
		} finally {
			setSaving(false);
		}
	}

	function openDeleteConfirm(user) {
		setUserToDelete(user);
	}

	async function confirmDelete() {
		if (!userToDelete) return;
		setDeleteLoading(true);
		try {
			await deleteUser(userToDelete.id);
			setAlert({
				type: "success",
				msg: "Usuario desactivado correctamente.",
			});
			setUserToDelete(null);
			await refresh();
		} catch {
			setAlert({
				type: "danger",
				msg: "No se pudo desactivar el usuario.",
			});
		} finally {
			setDeleteLoading(false);
		}
	}

	function openActivateConfirm(user) {
		setActivateError("");
		setUserToActivate(user);
	}

	async function confirmActivate() {
		if (!userToActivate) return;
		if (isUserReactivationBlocked(userToActivate, companies)) {
			setActivateError(
				"Este usuario pertenece a una organización desactivada. Primero debes activar la organización.",
			);
			return;
		}
		setActivateLoading(true);
		try {
			await updateUser(userToActivate.id, { is_active: true });
			setAlert({
				type: "success",
				msg: "Usuario reactivado correctamente.",
			});
			setUserToActivate(null);
			await refresh();
		} catch (e) {
			setActivateError(
				e?.response?.data?.detail ||
				"No se pudo reactivar el usuario.",
			);
		} finally {
			setActivateLoading(false);
		}
	}

	const detailOrganization = useMemo(
		() => getOrganizationData(detailData?.student || detailUser, companies),
		[detailData?.student, detailUser, companies],
	);

	const detailRoleLabel = ROLE_DETAIL_LABEL[detailUser?.role] || "usuario";
	const detailStudent = detailData?.student
		? { ...detailUser, ...detailData.student, role: detailData.student.role || detailUser?.role }
		: detailUser;
	const studentSummary = detailData?.summary || {
		enrollments_count: 0,
		certificates_count: 0,
		batches_count: 0,
	};
	const studentEnrollments = Array.isArray(detailData?.enrollments)
		? detailData.enrollments
		: [];
	const studentBatches = Array.isArray(detailData?.batches)
		? detailData.batches
		: [];

	useEffect(() => {
		let cancelled = false;
		setDetailError("");
		setDetailData(null);

		if (!detailUser || detailUser.role !== "student") {
			setDetailLoading(false);
			return () => {
				cancelled = true;
			};
		}

		setDetailLoading(true);
		(async () => {
			try {
				const payload = await getStudentProfile(detailUser.id);
				if (!cancelled) setDetailData(payload);
			} catch (e) {
				if (!cancelled) {
					setDetailError(
						e?.response?.data?.detail ||
						"No fue posible cargar el detalle extendido del estudiante.",
					);
				}
			} finally {
				if (!cancelled) setDetailLoading(false);
			}
		})();

		return () => {
			cancelled = true;
		};
	}, [detailUser]);

	return (
		<div className="container-fluid">
			<div className="container-principal">
				<div>
					<h2 className="m-0">Usuarios</h2>
				</div>

				<div className="admin-actions-wrapper w-100">
					<button
						className="admin-btn text-nowrap"
						onClick={openCreate}
					>
						<span>Nuevo usuario</span>
					</button>
				</div>
			</div>

			{alert && (
				<div
					className={`alert alert-${alert.type} mt-3 mb-0`}
					role="alert"
				>
					<span>{alert.msg}</span>
				</div>
			)}
			<ul className="nav nav-tabs mt-4">
				<li className="nav-item">
					<button
						className={`nav-link ${activeTab === "admin" ? "active" : ""}`}
						onClick={() => setActiveTab("admin")}
					>
						<span>Administradores</span>
					</button>
				</li>
				<li className="nav-item">
					<button
						className={`nav-link ${activeTab === "company_admin" ? "active" : ""}`}
						onClick={() => setActiveTab("company_admin")}
					>
						<span>Organizaciones</span>
					</button>
				</li>
				<li className="nav-item">
					<button
						className={`nav-link ${activeTab === "teacher" ? "active" : ""}`}
						onClick={() => setActiveTab("teacher")}
					>
						<span>Docentes</span>
					</button>
				</li>
				<li className="nav-item">
					<button
						className={`nav-link ${activeTab === "student" ? "active" : ""}`}
						onClick={() => setActiveTab("student")}
					>
						<span>Estudiantes</span>
					</button>
				</li>
				<li className="nav-item">
					<button
						className={`nav-link ${activeTab === "" ? "active" : ""}`}
						onClick={() => setActiveTab("")}
					>
						<span>Sin rol asignado</span>
					</button>
				</li>
			</ul>
			<div className="card mt-3 shadow-sm">
				<div className="card-body p-0">
					<div className="table-toolbar table-toolbar-locked">
						<div className="toolbar-group toolbar-pagination-group">
							<button
								type="button"
								className="toolbar-nav-btn"
								onClick={() => setCurrentPage(1)}
								disabled={currentPage === 1}
							>
								&lt;&lt;
							</button>
							<button
								type="button"
								className="toolbar-nav-btn"
								onClick={() =>
									setCurrentPage((prev) =>
										Math.max(1, prev - 1),
									)
								}
								disabled={currentPage === 1}
							>
								&lt;
							</button>
							<select
								className="form-select toolbar-select toolbar-page-select"
								value={currentPage}
								onChange={(e) =>
									setCurrentPage(Number(e.target.value))
								}
								disabled={totalPages <= 1}
							>
								{Array.from(
									{ length: totalPages },
									(_, idx) => idx + 1,
								).map((page) => (
									<option key={page} value={page}>
										{page}
									</option>
								))}
							</select>
							<button
								type="button"
								className="toolbar-nav-btn"
								onClick={() =>
									setCurrentPage((prev) =>
										Math.min(totalPages, prev + 1),
									)
								}
								disabled={currentPage >= totalPages}
							>
								&gt;
							</button>
							<button
								type="button"
								className="toolbar-nav-btn"
								onClick={() => setCurrentPage(totalPages)}
								disabled={currentPage >= totalPages}
							>
								&gt;&gt;
							</button>
						</div>

						<div className="search-group toolbar-search-group d-flex align-items-center gap-2">
							<input
								className="form-control"
								value={search} onChange={(e) => setSearch(e.target.value)}
								placeholder="Buscar por RUT, nombre, rol u organización"
								maxLength={120}
							/>
							<button
								type="button"
								className="toolbar-nav-btn"
								onClick={() => {
									setSearch("");
									setCurrentPage(1);
								}}
								disabled={!search.trim()}
								title="Limpiar búsqueda"
								aria-label="Limpiar búsqueda"
							>
								<i className="ri-eraser-line" />
							</button>
						</div>

						<div className="toolbar-group toolbar-controls-group">
							<label className="d-flex align-items-center gap-2 mb-0">
								<span>Número de filas:</span>
								<select
									className="form-select toolbar-select"
									value={rowsPerPage}
									onChange={(e) =>
										setRowsPerPage(Number(e.target.value))
									}
								>
									<option value={10}>10</option>
									<option value={25}>25</option>
									<option value={50}>50</option>
									<option value={100}>100</option>
								</select>
							</label>

							<label className="d-flex align-items-center gap-2 mb-0">
								<span>Ordenar por:</span>
								<select
									className="form-select toolbar-select"
									value={sortField}
									onChange={(e) =>
										setSortField(e.target.value)
									}
								>
									{USER_SORT_OPTIONS.map((option) => (
										<option
											key={option.value}
											value={option.value}
										>
											{option.label}
										</option>
									))}
								</select>
							</label>

							<button
								type="button"
								className="toolbar-order-btn"
								onClick={() =>
									setSortDirection((prev) =>
										prev === "asc" ? "desc" : "asc",
									)
								}
							>
								{sortDirection === "asc" ? "Asc" : "Desc"}
							</button>

							<label className="d-flex align-items-center gap-2 mb-0">
								<input
									className="form-check-input m-0"
									type="checkbox"
									checked={showInactiveUsers}
									onChange={(e) =>
										setShowInactiveUsers(e.target.checked)
									}
								/>
								<span>Mostrar inactivos</span>
							</label>

							<button
								type="button"
								className="toolbar-nav-btn"
								onClick={resetTableState}
								title="Reiniciar tabla"
							>
								Reiniciar
							</button>
						</div>
					</div>

					<div className="table-responsive table-wrapper-scroll">
						<table className="table table-sm table-hover mb-0 align-middle table-organizations-nowrap">
							<thead className="table-light">
								<tr>
									<th>
										<span>RUT</span>
									</th>
									<th>
										<span>Nombre</span>
									</th>
									<th>
										<span>Rol</span>
									</th>
									<th>
										<span>Organización</span>
									</th>
									<th>
										<span>Activo</span>
									</th>
									<th
										className="text-center"
										style={{ width: 220 }}
									>
										<span>Acciones</span>
									</th>
								</tr>
							</thead>
							<tbody>
								{loading ? (
									<tr>
										<td
											colSpan="6"
											className="text-muted p-4"
										>
											<span>Cargando...</span>
										</td>
									</tr>
								) : usersToShow.length === 0 ? (
									<tr>
										<td
											colSpan="6"
											className="text-muted p-4"
										>
											<span>No hay usuarios con este rol</span>
										</td>
									</tr>
								) : (
									usersToShow.map((u) => (
										<tr key={u.id}>
											<td>
												<span>
													<strong>
														{highlightRutSearchMatch(
															formatRut(u.rut) ||
															u.rut,
															search,
														)}
													</strong>
												</span>
											</td>
											<td>
												<span>
													{highlightSearchMatch(
														capitalizeFirstLetter(
															(u.first_name ||
																"") +
															" " +
															(u.last_name ||
																""),
														),
														search,
													)}
												</span>
											</td>
											<td>
												<span>
													{ROLE_LABEL[u.role] ||
														u.role ||
														"-"}
												</span>
											</td>
											<td>
												<span>
													{highlightSearchMatch(
														getOrganizationDisplayName(
															u,
															companies,
														),
														search,
													)}
												</span>
											</td>
											<td>
												<StatusBadge
													active={Boolean(
														u.is_active,
													)}
													title="Usuario activo"
												/>
											</td>
											<td>
												<div className="d-flex justify-content-center align-items-center gap-2 flex-nowrap">
													<button
														className="btn-sm adm-see-btn d-inline-flex align-items-center justify-content-center border-0"
														onClick={() => setDetailUser(u)}
														disabled={u.is_active === false}
														title={
															u.is_active === false
																? "Primero debes reactivar el usuario."
																: "Detalle"
														}
														aria-label="Detalle"
													>
														<i className="ri-eye-fill" />
													</button>
													<button
														className="btn-sm adm-upd-btn d-inline-flex align-items-center justify-content-center border-0"
														onClick={() =>
															openEdit(u)
														}
														disabled={u.is_active === false}
														title={
															u.is_active === false
																? "Primero debes reactivar el usuario."
																: "Editar"
														}
														aria-label="Editar"
													>
														<i className="ri-edit-fill" />
													</button>
													<button
														className="btn-sm adm-del-btn d-inline-flex align-items-center justify-content-center border-0"
														onClick={() =>
															openDeleteConfirm(u)
														}
														disabled={u.is_active === false}
														title={
															u.is_active === false
																? "El usuario ya está desactivado."
																: "Desactivar"
														}
														aria-label="Desactivar"
													>
														<i className="ri-close-circle-line" />
													</button>
													<button
														className="btn-sm adm-upd-btn d-inline-flex align-items-center justify-content-center border-0"
														onClick={() =>
															openActivateConfirm(u)
														}
														disabled={
															u.is_active !== false ||
															isUserReactivationBlocked(
																u,
																companies,
															)
														}
														title={
															u.is_active !== false
																? "El usuario ya está activo."
																: isUserReactivationBlocked(
																	u,
																	companies,
																)
																	? "Primero debes activar la organización."
																	: "Reactivar"
														}
														aria-label="Reactivar"
													>
														<i className="ri-checkbox-circle-line" />
													</button>
												</div>
											</td>
										</tr>
									))
								)}
							</tbody>
						</table>
					</div>
					<div className="table-footer-summary">
						<span>
							Mostrando {totalRows === 0 ? 0 : startIndex + 1} -{" "}
							{endIndex} de {totalRows} registros
						</span>
					</div>
				</div>
			</div>

			<Modal
				title={editing ? "Editar usuario" : "Nuevo usuario"}
				open={modalOpen}
				onClose={closeModal}
				footer={
					<>
						{editing && canResetPassword(editing) && !credentialResult && (
							<button
								type="button"
								className="btn-sm adm-upd-btn"
								onClick={() => askResetPassword(editing)}
								disabled={saving || resetPasswordLoading}
							>
								<span>Reiniciar contraseña</span>
							</button>
						)}
						<button
							className="btn-sm adm-del-btn"
							onClick={closeModal}
							disabled={saving}
						>
							<span>
								{credentialResult ? "Cerrar" : "Cancelar"}
							</span>
						</button>
						{!credentialResult && (
							<button
								className="btn-sm adm-save-btn"
								onClick={onSubmit}
								disabled={saving}
							>
								<span>
									{saving ? "Guardando..." : "Guardar"}
								</span>
							</button>
						)}
					</>
				}
			>
				{alert && (
					<div className={`alert alert-${alert.type}`} role="alert">
						<span>{alert.msg}</span>
					</div>
				)}

				{credentialResult ? (
					<div>

						<div className="alert alert-success mb-0 text-center py-4" role="alert">
							<i className="ri-mail-send-line d-block mb-3" style={{ fontSize: "2.5rem", color: "#198754" }}></i>
							<div className="fw-semibold mb-2 fs-5">
								<span>Perfecto, las credenciales fueron enviadas por correo electrónico</span>
							</div>
							<p className="mb-0 text-muted small">
								<span>
									El usuario recibirá un mensaje con su usuario y contraseña temporal. La primera vez que ingrese deberá cambiarla obligatoriamente.
								</span>
							</p>
						</div>
					</div>
				) : (
					<form onSubmit={onSubmit} className="row g-3">
						{!editing && (
							<div className="col-12">
								<p className="text-muted small mb-0">
									<span>
										Completa los datos. Recibirá usuario y
										contraseña temporal; la primera vez que
										ingrese deberá cambiarla.
									</span>
								</p>
							</div>
						)}
						{editing && (
							<div className="col-12">
								<div className="alert alert-light border mb-0 py-2 px-3 small">
									<span>
										El nombre de usuario es generado
										automáticamente por el sistema. Si
										cambias nombres o apellidos, se
										recalcula siguiendo la misma regla
										automática.
									</span>
								</div>
							</div>
						)}
						{editing && resetPasswordResult && (
							<div className="col-12">
								<div className="alert alert-success mb-0 py-2 px-3 small">
									<div className="fw-semibold mb-1">
										<span>Contraseña temporal generada</span>
									</div>
									<div>
										<span>Usuario: </span>
										<code>{resetPasswordResult.username}</code>
									</div>
									<div>
										<span>Contraseña: </span>
										<code>{resetPasswordResult.tempPassword}</code>
									</div>
								</div>
							</div>
						)}
						<div className={getUserFieldColumnClass(editing)}>
							<label className="form-label">
								<span>Nombres </span>
								{!editing && (
									<span className="text-danger">*</span>
								)}
							</label>
							<input
								className={`form-control ${fieldErrors.first_name ? "is-invalid" : ""}`}
								value={form.first_name} onChange={(e) => {
									setForm((p) => ({
										...p,
										first_name: e.target.value,
									}));
									if (fieldErrors.first_name)
										setFieldErrors((p) => ({
											...p,
											first_name: undefined,
										}));
								}}
								placeholder="Ej: Juan Pablo"
								aria-invalid={!!fieldErrors.first_name}
							/>
							{fieldErrors.first_name && (
								<div className="invalid-feedback">
									{fieldErrors.first_name}
								</div>
							)}
						</div>
						<div className={getUserFieldColumnClass(editing)}>
							<label className="form-label">
								<span>Apellidos </span>
								{!editing && (
									<span className="text-danger">*</span>
								)}
							</label>
							<input
								className={`form-control ${fieldErrors.last_name ? "is-invalid" : ""}`}
								value={form.last_name} onChange={(e) => {
									setForm((p) => ({
										...p,
										last_name: e.target.value,
									}));
									if (fieldErrors.last_name)
										setFieldErrors((p) => ({
											...p,
											last_name: undefined,
										}));
								}}
								placeholder="Ej: Perez Soto"
								aria-invalid={!!fieldErrors.last_name}
							/>
							{fieldErrors.last_name && (
								<div className="invalid-feedback">
									{fieldErrors.last_name}
								</div>
							)}
						</div>

						<div className="col-md-6">
							<label className="form-label">
								<span>Email </span>
								{!editing && (
									<span className="text-danger">*</span>
								)}
							</label>
							<input
								type="email"
								className={`form-control ${fieldErrors.email ? "is-invalid" : ""}`}
								value={form.email} onChange={(e) => {
									setForm((p) => ({
										...p,
										email: e.target.value,
									}));
									if (fieldErrors.email)
										setFieldErrors((p) => ({
											...p,
											email: undefined,
										}));
								}}
								placeholder="Ej: juan.perez@correo.cl"
								aria-invalid={!!fieldErrors.email}
							/>
							{fieldErrors.email && (
								<div className="invalid-feedback">
									{fieldErrors.email}
								</div>
							)}
						</div>
						<div className="col-md-6">
							<label className="form-label">
								<span>RUT </span>
								{!editing && (
									<span className="text-danger">*</span>
								)}
							</label>
							<input
								id="userRut"
								className={`form-control ${rutError || fieldErrors.rut ? "is-invalid" : ""}`}
								value={form.rut} onChange={(e) => {
									setForm((p) => ({
										...p,
										rut: sanitizeRutDigitsOnly(
											e.target.value,
										),
									}));
									if (rutError || fieldErrors.rut) {
										if (!e.target.value.trim()) {
											setRutError(REQUIRED_RUT_MESSAGE);
											setFieldErrors((p) => ({
												...p,
												rut: REQUIRED_RUT_MESSAGE,
											}));
											return;
										}
										setRutError("");
										setFieldErrors((p) => ({
											...p,
											rut: undefined,
										}));
									}
								}}
								onBlur={() => {
									if (!form.rut?.trim()) {
										setRutError(REQUIRED_RUT_MESSAGE);
										setFieldErrors((p) => ({
											...p,
											rut: REQUIRED_RUT_MESSAGE,
										}));
										return;
									}
									const r = commitRutField(form.rut);
									if (!r.ok) {
										setRutError(
											r.message || INVALID_RUT_MESSAGE,
										);
										setFieldErrors((p) => ({
											...p,
											rut:
												r.message ||
												INVALID_RUT_MESSAGE,
										}));
									} else {
										setForm((p) => ({
											...p,
											rut: r.formatted,
										}));
										setRutError("");
										setFieldErrors((p) => ({
											...p,
											rut: undefined,
										}));
									}
								}}
								onKeyDown={(e) => {
									if (e.key === "Enter") {
										e.preventDefault();
										e.currentTarget.blur();
									}
								}}
								maxLength={10}
								placeholder="Ej: 123456789"
								title={RUT_DIGITS_HINT}
								aria-invalid={!!(rutError || fieldErrors.rut)}
								aria-describedby={
									rutError || fieldErrors.rut
										? "userRutFeedback"
										: undefined
								}
							/>
							<p className="form-text small text-muted mb-0">
								{RUT_DIGITS_HINT}
							</p>
							{(rutError || fieldErrors.rut) && (
								<div
									id="userRutFeedback"
									className="invalid-feedback"
								>
									{rutError || fieldErrors.rut}
								</div>
							)}
						</div>

						<div className="col-md-6">
							<label className="form-label">
								<span>Rol principal </span>
								{!editing && (
									<span className="text-danger">*</span>
								)}
							</label>
							<select
								className={`form-select ${fieldErrors.role ? "is-invalid" : ""}`}
								value={form.role || ""}
								onChange={(e) => {
									setForm((p) => ({
										...p,
										role: e.target.value || null,
									}));
									if (fieldErrors.role)
										setFieldErrors((p) => ({
											...p,
											role: undefined,
										}));
								}}
								aria-invalid={!!fieldErrors.role}
							>
								{editing ? (
									<>
										<option value="">
											Sin rol preferido
										</option>
										<option value="admin">
											Admin
										</option>
										<option value="teacher">
											Docente
										</option>
										<option value="student">
											Estudiante
										</option>
										<option value="company_admin">
											Organización
										</option>
									</>
								) : (
									<>
										<option value="">
											Selecciona un rol
										</option>
										<option value="admin">
											Administrador
										</option>
										<option value="teacher">
											Docente
										</option>
									</>
								)}
							</select>
							{fieldErrors.role && (
								<div className="invalid-feedback">
									{fieldErrors.role}
								</div>
							)}
							<span className="text-muted d-block mt-1 fs-6">
								{editing
									? "Este rol define qué verá el usuario al entrar."
									: "Solo puedes crear administradores y docentes aquí. Estudiantes y encargados de organización se crean desde otros módulos."}
							</span>
						</div>
						<div className="col-md-6">
							<label className="form-label">
								<span>
									Organización{" "}
									<small className="text-muted mt-1 fs-6">
										(Opcional)
									</small>
								</span>
							</label>
							<select
								className={`form-select ${fieldErrors.company ? "is-invalid" : ""}`}
								value={form.company || ""}
								onChange={(e) => {
									setForm((p) => ({
										...p,
										company: e.target.value
											? parseInt(e.target.value)
											: "",
									}));
									if (fieldErrors.company) {
										setFieldErrors((p) => ({
											...p,
											company: undefined,
										}));
									}
								}}
								disabled={
									form.role === "admin" ||
									form.role === "teacher"
								}
							>
								{form.role === "admin" ||
									form.role === "teacher" ? (
									<option value="">Somnus</option>
								) : (
									<>
										<option value="">
											Sin organización
										</option>
										{selectableCompanies.map((c) => (
											<option key={c.id} value={c.id}>
												{capitalizeFirstLetter(c.name)}{" "}
												— {formatRut(c.rut) || c.rut}
												{!c.is_active
													? " (desactivada)"
													: ""}
											</option>
										))}
									</>
								)}
							</select>
						</div>

						<div className="col-md-6 d-flex align-items-end">
							<div className="form-check">
								<input
									className="form-check-input"
									type="checkbox"
									checked={form.is_active}
									onChange={(e) =>
										setForm((p) => ({
											...p,
											is_active: e.target.checked,
										}))
									}
									id="userActive"
								/>
								<label
									className="form-check-label"
									htmlFor="userActive"
								>
									<span>Usuario activo</span>
								</label>
							</div>
						</div>

						<button type="submit" className="d-none">
							<span>enviar</span>
						</button>
					</form>
				)}
			</Modal>

			<Modal
				title="Ficha de usuario"
				open={Boolean(detailUser)}
				onClose={() => setDetailUser(null)}
				dialogClassName="modal-dialog modal-dialog-centered modal-lg"
				footer={
					<button
						className="btn-sm adm-del-btn"
						onClick={() => setDetailUser(null)}
					>
						<span>Cerrar</span>
					</button>
				}
			>
				{detailUser && (
					<>
						<section className="card card-table shadow-sm">
							<div className="card-body p-4">
								<div className="d-flex align-items-center justify-content-between border-bottom flex-wrap gap-2 mb-4 pb-2">
									<h5 className="mb-0 fw-bold">Datos {detailRoleLabel}</h5>
									<StatusBadge
										active={Boolean(detailStudent?.is_active)}
										trueLabel="Activo"
										falseLabel="Inactivo"
										title="Estado del usuario"
									/>
								</div>

								<div className="container-fluid p-0">
									<div className="row g-3">
										<div className="col-md-4 pb-2 d-flex flex-wrap justify-content-start">
											<label className="mb-1 me-2 fw-semibold">Nombre:</label>
											<span>
												{capitalizeFirstLetter(`${detailStudent?.first_name || ""} ${detailStudent?.last_name || ""}`.trim()) || "-"}
											</span>
										</div>
										<div className="col-md-4 pb-2 d-flex flex-wrap justify-content-start justify-content-md-center">
											<label className="mb-1 me-2 fw-semibold">RUT:</label>
											<span>{formatRut(detailStudent?.rut) || detailStudent?.rut || "-"}</span>
										</div>
										<div className="col-md-4 pb-2 d-flex flex-wrap justify-content-start justify-content-md-center">
											<label className="mb-1 me-2 fw-semibold">Usuario:</label>
											<span>{detailStudent?.username || "-"}</span>
										</div>
										<div className="col-md-8 pb-2 d-flex flex-wrap justify-content-start">
											<label className="mb-1 me-2 fw-semibold">Correo electrónico:</label>
											<span className="text-break">{detailStudent?.email || "-"}</span>
										</div>
										<div className="col-md-4 pb-2 d-flex flex-wrap justify-content-start justify-content-md-center">
											<label className="mb-1 me-2 fw-semibold">Rol:</label>
											<span>{ROLE_LABEL[detailStudent?.role] || detailStudent?.role || "-"}</span>
										</div>

										<h5 className="mb-0 fw-bold border-bottom pb-2">Datos Organización</h5>

										<div className="col-md-6 pb-2 d-flex flex-wrap justify-content-start">
											<label className="mb-1 me-2 fw-semibold">Nombre:</label>
											<span>{detailOrganization.name}</span>
										</div>
										<div className="col-md-6 pb-2 d-flex flex-wrap justify-content-start justify-content-md-center">
											<label className="mb-1 me-2 fw-semibold">RUT:</label>
											<span>{detailOrganization.rut}</span>
										</div>
									</div>
								</div>
							</div>
						</section>

						{detailStudent?.role === "student" && (
							<>
								{detailLoading && (
									<div className="alert alert-info mt-3 mb-0" role="alert">
										<span>Cargando detalle extendido del estudiante...</span>
									</div>
								)}

								{detailError && (
									<div className="alert alert-warning mt-3 mb-0" role="alert">
										<span>{detailError}</span>
									</div>
								)}

								{!detailLoading && !detailError && detailData && (
									<>
										<div className="row g-3 mt-1 student-profile-kpis">
											<div className="col-md-4">
												<div className="card card-table shadow-sm matri">
													<div className="card-body">
														<div><span className="fw-semibold">Matrículas</span></div>
														<div className="fs-4 fw-semibold"><span>{studentSummary.enrollments_count}</span></div>
													</div>
												</div>
											</div>
											<div className="col-md-4">
												<div className="card card-table shadow-sm certi">
													<div className="card-body">
														<div><span className="fw-semibold">Certificados</span></div>
														<div className="fs-4 fw-semibold"><span>{studentSummary.certificates_count}</span></div>
													</div>
												</div>
											</div>
											<div className="col-md-4">
												<div className="card card-table shadow-sm lote">
													<div className="card-body">
														<div><span className="fw-semibold">Lotes de carga</span></div>
														<div className="fs-4 fw-semibold"><span>{studentSummary.batches_count}</span></div>
													</div>
												</div>
											</div>
										</div>

										<div className="card card-table mt-4 shadow-sm">
											<div className="card-body p-4">
												<div className="pb-2 border-bottom fw-semibold"><h5>Historial académico</h5></div>
												<div className="table-responsive table-wrapper-scroll">
													<table className="table table-sm table-hover mb-0 align-middle table-organizations-nowrap">
														<thead className="table-light">
															<tr>
																<th><span>Curso</span></th>
																<th><span>Estado curso</span></th>
																<th><span>Estado matrícula</span></th>
																<th><span>Avance</span></th>
																<th><span>Último examen</span></th>
																<th><span>Certificado</span></th>
																<th><span>Fecha matrícula</span></th>
															</tr>
														</thead>
														<tbody>
															{studentEnrollments.length === 0 ? (
																<tr>
																	<td colSpan="7" className="text-muted p-3"><span>Sin matriculas registradas.</span></td>
																</tr>
															) : (
																studentEnrollments.map((item) => (
																	<tr key={item.id}>
																		<td>
																			<strong><span>{item.course?.code || "-"}</span></strong>
																			<div className="small text-muted"><span>{capitalizeFirstLetter(item.course?.name) || "-"}</span></div>
																		</td>
																		<td><span className={statusChipClass(item.course?.status)}>{translateStatusLabel(item.course?.status)}</span></td>
																		<td><span className={statusChipClass(item.status)}>{translateStatusLabel(item.status)}</span></td>
																		<td>
																			<span>{item.progress?.percentage ?? 0}%</span>
																			<div className="small text-muted"><span>{item.progress?.viewed_contents ?? 0} / {item.progress?.total_contents ?? 0} recursos</span></div>
																		</td>
																		<td>
																			{item.latest_exam_attempt ? (
																				<>
																					<div><span>Puntaje: {item.latest_exam_attempt.score ?? "-"}</span></div>
																					<div className="small mt-1"><span className={statusChipClass(item.latest_exam_attempt.passed ? "passed" : "failed")}>{item.latest_exam_attempt.passed ? "Aprobado" : "No aprobado"}</span></div>
																				</>
																			) : (
																				<span>-</span>
																			)}
																		</td>
																		<td>
																			{item.certificate ? (
																				<>
																					<div><span className={statusChipClass(item.certificate.is_valid ? "completed" : "revoked")}>{item.certificate.is_valid ? "Válido" : "Revocado"}</span></div>
																					<div className="small text-muted"><span>{formatDate(item.certificate.issued_at) || "-"}</span></div>
																				</>
																			) : (
																				<span>-</span>
																			)}
																		</td>
																		<td><span>{formatDate(item.enrolled_at) || "-"}</span></td>
																	</tr>
																))
															)}
														</tbody>
													</table>
												</div>
											</div>
										</div>

										<div className="card card-table mt-4 shadow-sm mb-1 student-profile-table-card">
											<div className="card-body p-4">
												<div className="pb-2 border-bottom fw-semibold"><h5>Lotes de carga relacionados</h5></div>
												<div className="table-responsive table-wrapper-scroll">
													<table className="table table-sm table-hover mb-0 align-middle table-organizations-nowrap">
														<thead className="table-light">
															<tr>
																<th><span>ID lote</span></th>
																<th><span>Archivo</span></th>
																<th><span>Organización</span></th>
																<th><span>Subido por</span></th>
																<th><span>Fecha</span></th>
															</tr>
														</thead>
														<tbody>
															{studentBatches.length === 0 ? (
																<tr>
																	<td colSpan="5" className="text-muted p-3"><span>Sin lotes asociados.</span></td>
																</tr>
															) : (
																studentBatches.map((batch) => (
																	<tr key={batch.id}>
																		<td><span>{batch.id}</span></td>
																		<td><span>{batch.file_name || "-"}</span></td>
																		<td><span>{capitalizeWords(batch.company?.name) || "-"}</span></td>
																		<td><span>{capitalizeWords(batch.uploaded_by?.full_name) || capitalizeWords(batch.uploaded_by?.username) || "-"}</span></td>
																		<td><span>{formatDateTime(batch.created_at) || "-"}</span></td>
																	</tr>
																))
															)}
														</tbody>
													</table>
												</div>
											</div>
										</div>
									</>
								)}
							</>
						)}
					</>
				)}
			</Modal>

			<ConfirmModal
				open={!!userToDelete}
				onClose={() => !deleteLoading && setUserToDelete(null)}
				onConfirm={confirmDelete}
				title="Desactivar usuario"
				message={
					userToDelete ? (
						<span>
							¿Desactivar al usuario "{userToDelete.username}"? El
							registro y su historial se conservarán, pero no podrá
							iniciar sesión mientras esté inactivo.
						</span>
					) : (
						""
					)
				}
				confirmLabel={<span>Desactivar</span>}
				variant="danger"
				loading={deleteLoading}
			/>

			<ConfirmModal
				open={!!userToActivate}
				onClose={() => {
					if (!activateLoading) {
						setUserToActivate(null);
						setActivateError("");
					}
				}}
				onConfirm={confirmActivate}
				title="Reactivar usuario"
				message={
					userToActivate ? (
						<>
							<span>
								¿Reactivar al usuario "{userToActivate.username}"? Podrá
								volver a iniciar sesión y operar según su rol vigente.
							</span>
							{(activateError ||
								isUserReactivationBlocked(
									userToActivate,
									companies,
								)) && (
									<div className="alert alert-danger mt-3 mb-0" role="alert">
										<span>
											{activateError ||
												"Este usuario pertenece a una organización desactivada. Primero debes activar la organización."}
										</span>
									</div>
								)}
						</>
					) : (
						""
					)
				}
				confirmLabel={<span>Reactivar</span>}
				loading={activateLoading}
				confirmDisabled={
					userToActivate
						? isUserReactivationBlocked(userToActivate, companies)
						: false
				}
			/>

			<ConfirmModal
				open={!!resetPasswordTarget}
				onClose={() => !resetPasswordLoading && setResetPasswordTarget(null)}
				onConfirm={confirmResetPassword}
				title="Reiniciar contraseña"
				message={
					resetPasswordTarget
						? `Esto reiniciará la contraseña de ${capitalizeWords(
							resetPasswordTarget.first_name,
						)} ${capitalizeWords(
							resetPasswordTarget.last_name,
						)} y generará una temporal nueva. ¿Deseas continuar?`
						: ""
				}
				confirmLabel={
					resetPasswordLoading ? "Generando..." : "Reiniciar contraseña"
				}
				loading={resetPasswordLoading}
			/>
		</div>
	);
}
